/** @format */

import { produce } from "immer";
import { Locale } from "../../i18n/init";
import { isValidUuid } from "../../resources/ResourceFunctions";

export type ResourceUpdate = {
  updateValue: string;
  updateIn: (string | number)[];
};

export const gatherResourceUpdates = (
  output: any,
  fileLocale: Locale,
): ResourceUpdate[] => {
  const keyPath: (string | number)[] = [];

  const resourceUpdates: ResourceUpdate[] = [];

  try {
    // Loop through object and create array of updates
    const loopObject = (obj: any, locale: "en" | "cy"): void => {
      for (const key in obj) {
        let value = obj[key];

        if (typeof value === "object" && key !== "downloadFiles") {
          let _key = key.replace(/^_/, "");

          // Not sure what this does, if the value is UUID no resource update will ever be reached, so the new key is never used?
          if (!isNaN(Number(_key)) && (value.uuid || value.UUID)) {
            // UUIDS are treated as case insensitive
            _key = ((value.uuid ?? value.UUID) as string).toLowerCase();
          }

          keyPath.push(_key);

          if (value.TRANSLATE) {
            if (value.TRANSLATE?.SELECTS !== "[object Object]") {
              let tmp;

              if (
                value.TRANSLATE.SELECTS &&
                Array.isArray(value.TRANSLATE.SELECTS)
              ) {
                tmp = value.TRANSLATE.SELECTS.map(
                  (select: { OPTIONS: string[] } | string) =>
                    typeof select === "object" ? select.OPTIONS : select,
                );
              } else if (typeof value.TRANSLATE.SELECTS === "object") {
                tmp = [value.TRANSLATE.SELECTS.OPTIONS];
              } else if (value.TRANSLATE.SELECTS !== undefined) {
                tmp = value.TRANSLATE.SELECTS;
              } else {
                tmp = value.TRANSLATE;
              }

              resourceUpdates.push({
                updateIn: [...keyPath, locale],
                updateValue: tmp,
              });
            }
            keyPath.pop();
          } else {
            loopObject(value, locale);
          }
        }
      }
      keyPath.pop();
    };

    loopObject(output, fileLocale);
  } catch {
    throw Error("Failed to gather resource updates");
  }

  return resourceUpdates;
};

// Take key array, value to set and object and produce a new object with the field specified updated
export const updateResource = (
  updateIn: (string | number)[],
  updateValue: any,
  object: any,
): any => {
  let _updateIn = [...updateIn];
  let key: string | number = _updateIn.pop() ?? "";

  // Root of resource details is `Object`
  const actualKey = key === "root" ? "Object" : key;

  // If UUID then find the record instead of using as key
  // Key is updated to index of the record
  const resolvedKey =
    typeof actualKey === "string" &&
    Array.isArray(object) &&
    isValidUuid(actualKey)
      ? object.findIndex((item) => (item.uuid ?? item.UUID) === actualKey)
      : actualKey;

  let value: any;
  // If at bottom of key array then update the value against the key popped off
  if (_updateIn.length < 1) {
    value =
      typeof updateValue === "string"
        ? updateValue.replace(
            /<span\s+class="glossary-term">\s*<span\s+class="term">(.*?)<\/span>\s*<span\s+class="definition">(.*?)<\/span>\s*<\/span>/g,
            (match: string, p1, p2) => {
              // Check if definition is surrounded by quotes
              if (!p2.match(/^\(.*?\)$/))
                return match.replaceAll(
                  '"glossary-term"',
                  '"glossary-term error"',
                );

              const _p2 = p2.replace(/^\(/, "").replace(/\)$/, "");

              // Count the number of span tags
              const spanCount = match.match(/(<span.*?>)|(<\/span>)/g)?.length;
              if (spanCount !== 6)
                return match.replaceAll(
                  '"glossary-term"',
                  '"glossary-term error"',
                );

              return `<span data-glossary="${_p2}">${p1}</span>`;
            },
          )
        : updateValue;
  } else {
    // If not at bottom of key array then call function again one level down setting the current key to the returned value
    // Recursive algorithm resolves when at end of key array
    value = updateResource(
      _updateIn,
      updateValue,
      object?.[resolvedKey] ?? (Array.isArray(object) ? [] : {}),
    );
  }

  return produce(object, (draft: any) => {
    draft[resolvedKey] = value;
  });
};
